import React, { memo, useMemo } from 'react'
import FormattedPhoneExtension, {
  DEFAULT_PHONE_EXTENSION_FORMAT,
} from 'transfix-ui/components/FormattedPhoneExtension'
import FormattedPhoneNumber, {
  IFormattedPhoneNumberProps,
  DEFAULT_PHONE_NUMBER_FORMAT,
} from 'transfix-ui/components/FormattedPhoneNumber'
import { Tooltip, ITooltipProps } from 'transfix-ui/components/Tooltip'
import { Link, makeStyles, Typography } from 'transfix-ui/core'
import { PhoneIcon } from 'transfix-icons'
import { DISPLAY } from 'constants/display'
import { useLastPhoneCall, useStartPhoneCall } from './hooks'
import { formatLastPhoneCall } from './utils'

const useConnectedPhoneNumberStyles = makeStyles(theme => ({
  tooltip: {
    padding: theme.spacing(1),
    textAlign: 'center',
    width: 350,
  },
}))

export interface IConnectedPhoneNumberProps
  extends IFormattedPhoneNumberProps,
    Pick<ITooltipProps, 'place'> {
  /**
   * @description Show tooltip with details about last call placed to `phoneNumber`.
   * @default true
   */
  showToolTip?: boolean
}

/**
 * @description FOR BROKERAGE USE ONLY. Displays a formatted phone number with click-to-call functionality
 *  enabled if third party (e.g., Five9) connection data is supplied. Additionally, it fetches and displays the last
 *  phone call made to the phone number via an on-hover toolip.
 */
export const ConnectedPhoneNumber = memo<IConnectedPhoneNumberProps>(
  ({
    dataTest,
    phoneExtension,
    phoneNumber,
    showToolTip = true,
    phoneNumberFormat = DEFAULT_PHONE_NUMBER_FORMAT,
    phoneExtensionFormat = DEFAULT_PHONE_EXTENSION_FORMAT,
    place = 'top',
  }) => {
    const classes = useConnectedPhoneNumberStyles()

    const {
      fetchLastPhoneCall,
      hasFetchedLastPhoneCall,
      isFetchingLastPhoneCall,
      lastPhoneCall,
    } = useLastPhoneCall(phoneNumber)

    const { canStartPhoneCall, startPhoneCall } = useStartPhoneCall(phoneNumber)

    const tooltipText = useMemo(() => {
      if (isFetchingLastPhoneCall) {
        return 'Loading last call...'
      }

      if (hasFetchedLastPhoneCall) {
        return formatLastPhoneCall(lastPhoneCall) ?? 'No calls placed'
      }

      return DISPLAY.empty
    }, [hasFetchedLastPhoneCall, isFetchingLastPhoneCall, lastPhoneCall])

    return (
      <Tooltip
        id={`${phoneNumber}-id`}
        afterShow={fetchLastPhoneCall}
        className={classes.tooltip}
        disable={!showToolTip}
        effect='solid'
        place={place}
        trigger={
          <span data-test={dataTest}>
            {canStartPhoneCall ? (
              <>
                <Link data-test={`${dataTest}-link`} component='button' onClick={startPhoneCall}>
                  <PhoneIcon verticalAlign='baseline' />

                  <FormattedPhoneNumber
                    dataTest={`${dataTest}-display`}
                    phoneNumber={phoneNumber}
                    phoneNumberFormat={phoneNumberFormat}
                  />
                </Link>

                {/* Not using the built-in <FormattedPhoneNumber /> extension display in order to keep the extension
                from being hyperlinked. Five9 click-to-call doesn't support extensions and we don't want to give users
                the impression that it does. */}
                {phoneExtension && (
                  <>
                    &nbsp;
                    <FormattedPhoneExtension
                      className='text-secondary'
                      phoneExtension={phoneExtension}
                      phoneExtensionFormat={phoneExtensionFormat}
                    />
                  </>
                )}
              </>
            ) : (
              <FormattedPhoneNumber
                dataTest={`${dataTest}-display`}
                phoneNumber={phoneNumber}
                phoneNumberFormat={phoneNumberFormat}
                phoneExtension={phoneExtension}
                phoneExtensionFormat={phoneExtensionFormat}
              />
            )}
          </span>
        }>
        <Typography component='div' variant='body2'>
          {tooltipText}
        </Typography>
      </Tooltip>
    )
  }
)

export default ConnectedPhoneNumber
